import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/layout/Layout';
import Navbar from '../../components/layout/Navbar';
import StatCard from '../../components/common/StatCard';
import { getSystemStats } from '../../api/superAdminApi';
import { Download, Calendar, Activity, TrendingUp, IndianRupee } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line
} from 'recharts';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { format } from 'date-fns';

const SystemStats = () => {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7days'); // '7days', '30days', 'all'

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getSystemStats(); // Need to change API call or create wrapper, actually the previous import used getDashboard, I should restore getSystemStats import in superAdminApi.js if missing, wait I'll use axios directly here if not exported.
      setStats(res.data?.data || {});
    } catch {
      toast.error('Failed to load system stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleExportPDF = async () => {
    const element = document.getElementById('stats-content');
    if (!element) return;
    
    try {
      const toastId = toast.loading('Generating PDF... Please wait.');
      const canvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 10, pdfWidth, pdfHeight);
      pdf.save(`Digital_Bhishi_Stats_${format(new Date(), 'dd_MMM_yyyy')}.pdf`);
      
      toast.success('Stats exported to PDF successfully!', { id: toastId });
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  return (
    <Layout>
      <Navbar title="System Statistics" subtitle="Platform-wide analytics" onRefresh={fetchStats} loading={loading} />
      <div className="p-6 space-y-6">

        {/* Date Filter & Export */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
            {[
              { id: '7days', label: 'Last 7 Days' },
              { id: '30days', label: 'Last 30 Days' },
              { id: 'all', label: 'All Time' },
            ].map(range => (
              <button
                key={range.id}
                onClick={() => setDateRange(range.id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  dateRange === range.id 
                    ? 'bg-blue-50 text-blue-700 shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl shadow-lg shadow-blue-500/30 transition-all"
          >
            <Download size={16} /> Export to PDF
          </button>
        </div>

        <div id="stats-content" className="space-y-6 p-4 bg-gray-50 rounded-xl">
          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Daily Registrations Line Chart */}
            <div className="card">
              <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Activity size={18} className="text-teal-600" />
                Daily New Registrations
              </h2>
              <div className="h-64 w-full">
                {loading ? <div className="flex items-center justify-center h-full text-gray-300">Loading...</div> : (
                  <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={200}>
                    <LineChart data={stats?.daily_registrations || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{fontSize:11}} />
                      <YAxis tick={{fontSize:11}} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#0d9488" strokeWidth={3} dot={{r: 4}} activeDot={{r: 6}} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Monthly Collections Bar Chart */}
            <div className="card">
              <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp size={18} className="text-blue-600" />
                Monthly Payment Collections
              </h2>
              <div className="h-64 w-full">
                {loading ? <div className="flex items-center justify-center h-full text-gray-300">Loading...</div> : (
                  <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={200}>
                    <BarChart data={stats?.monthly_collections || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{fontSize:11}} />
                      <YAxis tick={{fontSize:11}} />
                      <Tooltip formatter={v=>[`₹${v.toLocaleString('en-IN')}`,'Amount']} />
                      <Bar dataKey="amount" fill="#2563eb" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* Summary Table */}
          <div className="card">
            <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Calendar size={18} className="text-purple-600" />
              System Summary
            </h2>
            {loading ? (
               <div className="h-20 bg-gray-100 rounded-xl animate-pulse" />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase bg-gray-50 border-b border-gray-100">
                      <th className="px-4 py-3 rounded-tl-lg">Metric</th>
                      <th className="px-4 py-3 rounded-tr-lg">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(stats?.summary || []).map((item, i) => (
                      <tr key={i} className="hover:bg-gray-50 transition-colors bg-white">
                        <td className="px-4 py-3 font-medium text-gray-900">{item.metric}</td>
                        <td className="px-4 py-3 font-bold text-blue-700">{item.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!stats?.summary || stats.summary.length === 0) && (
                  <p className="text-center text-gray-500 py-4 text-sm">No summary data available.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SystemStats;
